import { useEffect, useMemo, useState } from 'react'
import {
  removeAssignment,
  scoreEvents,
  subscribeToEvents,
  createEvent,
  updateEvent,
  upsertAssignment,
  type ScoredEvent,
} from './repository/eventsRepository'
import {
  EVENT_STAGES,
  EVENT_STAGE_LABEL,
  EVENT_STAGE_STYLES,
  EVENT_TIER_STYLES,
  isUnverified,
  type CrmEvent,
  type EventAssignment,
  type EventStage,
  type Person,
} from '@/types/events'
import { NewEventModal } from './NewEventModal'
import { ListMap, type MapRecord } from '@/shared/map/LazyListMap'
import { formatDate } from '@/shared/util/dates'
import { useAuth } from '@/features/auth/AuthProvider'
import { useTableState } from '@/shared/hooks/useTableState'
import { ColumnPicker, DataTable } from '@/shared/components/DataTable'
import { ViewToggle, useViewMode } from '@/shared/components/ViewToggle'
import { LinkedPosts } from '@/features/publishing/LinkedPosts'
import { KanbanBoard, type KanbanCard } from '@/shared/components/KanbanBoard'
import type { DataRow, FilterOptions } from '@/shared/components/DataTable'
import { matchesEventSearch } from './search'
import {
  buildCells,
  COLUMN_KEYS,
  COLUMN_META,
  DEFAULT_COLUMNS,
  FILTERABLE_COLUMNS,
  filterValue,
  isPast,
  passesFilters,
  sortEvents,
  type ColumnKey,
  type SortKey,
} from './table'
import './EventsWorkspace.css'

type View = 'pipeline' | 'calendar' | 'who' | 'verify' | 'tasks'

const VIEWS: { key: View; label: string }[] = [
  { key: 'pipeline', label: 'Pipeline' },
  { key: 'calendar', label: 'Calendar' },
  { key: 'who', label: 'Who goes where' },
  { key: 'tasks', label: 'Due soon' },
  { key: 'verify', label: 'To verify' },
]

const DAY = 86_400_000

const COLUMNS_KEY = 'skidaCrm.eventColumns'

/** Sort keys that read best highest-first — a score, a headcount. */
const DESCENDING_FIRST = ['score', 'axes', 'who']

/**
 * Events — the arenas we should be visible at, and who covers them.
 *
 * Scoring runs here on every render rather than being read from the documents.
 * The spec puts the derived fields under a nightly job; until that exists a
 * read-time projection cannot drift, and changing an assignment moves the
 * coverage immediately, which is the whole point of the number.
 */
export function EventsWorkspace() {
  const [raw, setRaw] = useState<{
    events: CrmEvent[]
    assignments: EventAssignment[]
    people: Person[]
  }>({ events: [], assignments: [], people: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<View>('pipeline')
  const [openId, setOpenId] = useState<string | null>(null)
  /** The event whose own details are being corrected — AVV-2026-001. */
  const [editing, setEditing] = useState<CrmEvent | null>(null)
  /** The empty form. Kept apart from `openId` so a create is never an edit. */
  const [creating, setCreating] = useState(false)
  const [showPast, setShowPast] = useState(false)
  const [term, setTerm] = useState('')
  const [viewMode, setViewMode] = useViewMode()
  /** The same filter the search box reaches with `mine`; the chip is a shortcut. */
  const [mineOnly, setMineOnly] = useState(false)

  const { user } = useAuth()
  const currentUser = user?.displayName ?? ''

  const t = useTableState<ColumnKey>({
    storageKey: COLUMNS_KEY,
    allKeys: COLUMN_KEYS,
    defaults: DEFAULT_COLUMNS,
    initialSort: 'score',
    initialDir: 'desc',
    descendingFirst: DESCENDING_FIRST,
  })

  useEffect(() => {
    const un = subscribeToEvents(
      (d) => {
        setRaw(d)
        setLoading(false)
      },
      (e) => {
        setError(e.message)
        setLoading(false)
      },
    )
    return un
  }, [])

  const scored = useMemo(
    () => scoreEvents(raw.events, raw.assignments, raw.people),
    [raw],
  )

  /**
   * Filter options are drawn from the whole book, not from what is currently
   * visible — a dropdown whose options disappear as you use it is a dropdown
   * you cannot undo.
   */
  const filterOptions = useMemo((): FilterOptions<ColumnKey> => {
    const distinct = (key: ColumnKey) =>
      [...new Set(scored.map((e) => filterValue(e, key)).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v }))
    return {
      status: EVENT_STAGES.map((s) => ({ value: s.key, label: s.label })),
      type: distinct('type'),
    }
  }, [scored])

  /** Search and column filters, applied everywhere a list of events is shown. */
  const searched = useMemo(
    () =>
      scored
        .filter((e) => matchesEventSearch(e, term, { currentUser }))
        .filter((e) => !mineOnly || matchesEventSearch(e, 'mine', { currentUser }))
        .filter((e) => passesFilters(e, t.filters)),
    [scored, term, currentUser, t.filters, mineOnly],
  )

  /** Discarded, unverified and — unless asked for — past events are held out. */
  const live = useMemo(() => {
    const rows = searched.filter((e) => {
      // The pipeline is the qualified book. Anything still unqualified — or
      // resting on an unverified source — sits behind "To verify" until
      // somebody has decided it is worth being there. A list that mixes the two
      // is a list nobody trusts the top of.
      if (e.stage === 'unqualified' || e.stage === 'discarded') return false
      if (isUnverified(e.sourceVerified)) return false
      if (!showPast && isPast(e)) return false
      return true
    })
    return sortEvents(rows, t.sortBy as SortKey, t.sortDir)
  }, [searched, showPast, t.sortBy, t.sortDir])

  const pastCount = useMemo(
    () => scored.filter((e) => isPast(e) && e.stage !== 'discarded').length,
    [scored],
  )

  /**
   * The filtered events, for the map — the one list where a place is the
   * point rather than an attribute.
   *
   * Colour is the event's stage (88), the same one the board colours by. Eight
   * of the 45 resolve to a venue and are exact; the rest are their country,
   * which for an event is a real answer rather than a shrug — the festival is
   * in Austria whether or not we know the street.
   */
  const mapRecords = useMemo(
    (): MapRecord[] =>
      live.map((e) => ({
        id: e.id,
        title: e.name,
        subtitle: [e.venue, e.country, e.startDate ? formatDate(e.startDate) : '']
          .filter(Boolean)
          .join(' · '),
        color: EVENT_STAGE_STYLES[e.stage].color,
        statusLabel: EVENT_STAGE_LABEL[e.stage],
        geo: e.geo,
        missingReason: e.country ? 'could not be placed' : 'no country on the record',
      })),
    [live],
  )

  /** Everything awaiting a human: not yet qualified, or not yet sourced. */
  const toVerify = useMemo(
    () =>
      sortEvents(
        searched
          .filter((e) => e.stage !== 'discarded')
          .filter((e) => e.stage === 'unqualified' || isUnverified(e.sourceVerified)),
        t.sortBy as SortKey,
        t.sortDir,
      ),
    [searched, t.sortBy, t.sortDir],
  )
  const parked = useMemo(() => scored.filter((e) => e.stage === 'discarded'), [scored])

  const due = useMemo(() => {
    const cutoff = new Date(Date.now() + 30 * DAY).toISOString().slice(0, 10)
    // Due soon is drawn from everything awaiting work, not from the qualified
    // list — the deadline that bites is on the ones nobody has looked at yet.
    return toVerify
      .filter((e) => e.outreachBy && e.outreachBy <= cutoff)
      .sort((a, b) => a.outreachBy.localeCompare(b.outreachBy))
  }, [toVerify])

  const open = openId ? scored.find((e) => e.id === openId) ?? null : null

  return (
    <main className="events">
      <div className="events__head">
        <div>
          <h2 className="events__title">Events — where we should be seen</h2>
          <p className="events__sub">
            {loading
              ? 'Loading…'
              : `${live.length} qualified · ${toVerify.length} to verify · ${parked.length} discarded` +
                (showPast || pastCount === 0 ? '' : ` · ${pastCount} past hidden`)}
          </p>
        </div>
        {/* Decision 71, built after 64 changed the columns: an event can be put
            in the book by hand now, rather than only by the importer. */}
        <button className="btn btn--primary" onClick={() => setCreating(true)}>
          + New event
        </button>
        <div className="events__tabs">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              className="chip"
              aria-pressed={view === v.key}
              onClick={() => setView(v.key)}
            >
              {v.label}
              {v.key === 'tasks' && due.length > 0 && ` (${due.length})`}
              {v.key === 'verify' && toVerify.length > 0 && ` (${toVerify.length})`}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="empty-note">Could not read events: {error}</div>}

      {view !== 'who' && (
        <div className="events__filters" onClick={() => t.setOpenFilter(null)}>
          <input
            className="input events__search"
            value={term}
            onChange={(ev) => setTerm(ev.target.value)}
            placeholder="Search — try mine, uncovered, undated, unverified, norway, a…"
            aria-label="Search events"
          />
          <button
            className="chip"
            aria-pressed={mineOnly}
            onClick={() => setMineOnly(!mineOnly)}
            title="Only events you own"
          >
            Mine
          </button>
          <ViewToggle value={viewMode} onChange={setViewMode} label="Events" withMap />
          {t.filterCount > 0 && (
            <button className="chip" onClick={t.clearAllFilters}>
              Clear {t.filterCount} filter{t.filterCount === 1 ? '' : 's'}
            </button>
          )}
          {(view === 'pipeline' || view === 'calendar') && (
            <button
              className="chip"
              aria-pressed={showPast}
              onClick={() => setShowPast(!showPast)}
              title="An event is historical once its last day has passed. Undated events are never hidden — not knowing when it runs is not the same as it being over."
            >
              {showPast ? 'Hiding nothing' : `Show past (${pastCount})`}
            </button>
          )}
          {view !== 'calendar' && (
            <div className="colpickwrap">
              <button
                className="chip"
                onClick={(ev) => {
                  ev.stopPropagation()
                  t.setShowPicker(!t.showPicker)
                }}
                aria-expanded={t.showPicker}
              >
                Columns ({t.columns.length})
              </button>
              {t.showPicker && (
                <ColumnPicker
                  meta={COLUMN_META}
                  columns={t.columns}
                  onToggle={t.toggleColumn}
                  onClose={() => t.setShowPicker(false)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {view === 'pipeline' && viewMode === 'map' && (
        <ListMap
          records={mapRecords}
          onOpen={setOpenId}
          emptyNote="No event matches these filters, so there is nothing to place."
        />
      )}

      {view === 'pipeline' && viewMode === 'kanban' && (
        <KanbanBoard
          columns={EVENT_STAGES.filter(
            (st) => st.key !== 'unqualified' && st.key !== 'discarded',
          ).map((st) => ({
            key: st.key,
            label: st.label,
            color: EVENT_STAGE_STYLES[st.key].color,
          }))}
          cards={live.map(toCard)}
          selectedId={openId}
          onSelect={setOpenId}
          emptyNote={
            toVerify.length > 0
              ? `Nothing qualified yet — all ${toVerify.length} events are waiting under “To verify”.`
              : 'No events in the calendar yet. Add one, or import the season list.'
          }
        />
      )}

      {view === 'pipeline' && viewMode === 'table' && (
        <EventTable
          rows={live}
          t={t}
          filterOptions={filterOptions}
          selectedId={openId}
          onOpen={setOpenId}
          loading={loading}
          empty={
            term.trim() || t.filterCount > 0
              ? 'No event matches that search. Try a shorter term, or clear it to see the season.'
              : toVerify.length > 0
                ? `Nothing qualified yet — all ${toVerify.length} events are waiting under “To verify”.`
                : showPast
                  ? 'No events in the calendar yet. Add one, or import the season list.'
                  : `No upcoming events. ${pastCount} past ones are hidden — show them to see the season behind you.`
          }
        />
      )}
      {view === 'tasks' && (
        <EventTable
          rows={due}
          t={t}
          filterOptions={filterOptions}
          selectedId={openId}
          onOpen={setOpenId}
          loading={loading}
          empty="Nothing falls due in the next 30 days that is still unqualified."
        />
      )}
      {view === 'verify' && (
        <>
          <p className="events__note">
            Everything still waiting on a person. Two things land here:{' '}
            <strong>unqualified</strong> events, which nobody has yet decided are worth being at,
            and events whose source still reads <code>NOT VERIFIED</code> — a placeholder must
            never look like a finding, so those are not scored at all. Set relevance and reach,
            replace the source line, then move the stage to Qualified and it joins the pipeline.
          </p>
          <EventTable
            rows={toVerify}
            t={t}
            filterOptions={filterOptions}
            selectedId={openId}
            onOpen={setOpenId}
            loading={loading}
            empty="Nothing waiting — every event is either qualified or discarded."
          />
        </>
      )}
      {view === 'calendar' && <Calendar rows={live} onOpen={setOpenId} />}
      {view === 'who' && <WhoGoesWhere rows={scored} people={raw.people} />}

      {open && (
        <EventDrawer
          e={open}
          people={raw.people}
          onClose={() => setOpenId(null)}
          onEdit={() => setEditing(open)}
        />
      )}

      {/* AVV-2026-001: the event's own details, correctable. Same form as the
          create one, opened on the record — see `NewEventModal`. */}
      {editing && (
        <NewEventModal
          event={editing}
          onClose={() => setEditing(null)}
          onCreate={async (draft) => {
            await updateEvent(editing.id, draft)
            setEditing(null)
          }}
        />
      )}
    
      {creating && (
        <NewEventModal
          onClose={() => setCreating(false)}
          onCreate={async (draft) => {
            const id = await createEvent(draft)
            setCreating(false)
            // Straight into the record: the form asks for what an event is,
            // and the rest of it is edited there.
            setOpenId(id)
          }}
        />
      )}

</main>
  )
}

/** One event as a Kanban card: identity, then the number that ranks it. */
function toCard(e: ScoredEvent): KanbanCard {
  const tier = e.computedTier ? EVENT_TIER_STYLES[e.computedTier] : null
  return {
    id: e.id,
    group: e.stage,
    title: e.name,
    meta: [e.country, e.startDate ? formatDate(e.startDate) : e.dateConfidence]
      .filter(Boolean)
      .join(' · '),
    badge:
      e.computedScore !== null && tier
        ? { text: `${e.computedScore} ${e.computedTier}`, bg: tier.bg, color: tier.color }
        : null,
    trailing: e.ownerName || e.ownerId || 'unassigned',
  }
}

/**
 * The event list.
 *
 * A mapping onto the shared DataTable and nothing more. This used to be a
 * hand-rolled grid with its own header buttons, its own caret and its own nine
 * fixed columns — a second, worse copy of the lead table sitting three
 * directories away from the first. Everything it did badly now happens in one
 * place, and the events side got a column picker and header filters for free.
 */
function EventTable({
  rows,
  t,
  filterOptions,
  selectedId,
  onOpen,
  loading,
  empty = 'Nothing to show in this group yet.',
}: {
  rows: ScoredEvent[]
  t: ReturnType<typeof useTableState<ColumnKey>>
  filterOptions: FilterOptions<ColumnKey>
  selectedId: string | null
  onOpen: (id: string) => void
  loading?: boolean
  empty?: string
}) {
  const dataRows: DataRow<ColumnKey>[] = rows.map((e) => {
    const st = EVENT_STAGE_STYLES[e.stage]
    // Both dates when they differ, one when they do not — "12. mars – 12. mars"
    // reads as a mistake rather than as a one-day event.
    const dates = [
      e.startDate && formatDate(e.startDate),
      e.endDate && e.endDate !== e.startDate && formatDate(e.endDate),
    ]
      .filter(Boolean)
      .join(' – ')

    return {
      id: e.id,
      title: e.name,
      subtitle: `${e.id} · ${e.category}${e.region ? ` · ${e.region}` : ''}`,
      // An unverified source is the one thing about an event that should be
      // visible before anybody opens it — it means nothing on the row is trusted.
      flag: isUnverified(e.sourceVerified) ? 'warning' : null,
      muted: isPast(e),
      cells: buildCells(e, t.columns),
      // On a phone the two lines are when and where it is, and who owns it —
      // which is the decision, and neither is a column at its default width.
      card: {
        status: {
          label: EVENT_STAGES.find((s) => s.key === e.stage)?.label ?? e.stage,
          bg: st.bg,
          color: st.color,
        },
        lines: [
          [dates || 'no date', e.venue || e.region || e.country].filter(Boolean).join(' · '),
          e.ownerName ? `Owner: ${e.ownerName}` : 'Unassigned',
        ],
      },
    }
  })

  return (
    <DataTable
      meta={COLUMN_META}
      columns={t.columns}
      rows={dataRows}
      identityLabel="Event"
      identitySortKey="name"
      sortBy={t.sortBy}
      sortDir={t.sortDir}
      onSort={t.onSort}
      filterable={FILTERABLE_COLUMNS}
      filters={t.filters}
      filterOptions={filterOptions}
      onToggleFilter={t.toggleFilter}
      onClearFilter={t.clearFilter}
      openFilter={t.openFilter}
      onOpenFilter={t.setOpenFilter}
      selectedId={selectedId}
      onSelect={onOpen}
      loading={loading}
      loadingNote="Loading events…"
      emptyNote={empty}
    />
  )
}

/**
 * The calendar is laid out by `outreachBy`, not by the event date.
 *
 * The spec is explicit about this and it is the right call: the deadline that
 * governs the work is when the approach has to be made, and a festival in April
 * whose partner packages are sold in December is not an April job.
 */
function Calendar({ rows, onOpen }: { rows: ScoredEvent[]; onOpen: (id: string) => void }) {
  const months = useMemo(() => {
    const m = new Map<string, ScoredEvent[]>()
    for (const e of rows) {
      const key = (e.outreachBy || e.startDate || '').slice(0, 7) || 'undated'
      m.set(key, [...(m.get(key) ?? []), e])
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [rows])

  return (
    <div className="events__cal">
      <p className="events__note">
        Laid out by <strong>outreach deadline</strong>, not by the event date — that is the date
        the work has to happen on. A festival in April whose partner packages are sold in December
        is a December job.
      </p>
      {months.map(([month, list]) => (
        <div className="events__month" key={month}>
          <div className="events__monthhead">
            {month === 'undated' ? 'No date yet' : formatMonth(month)}
            <span className="events__monthcount">{list.length}</span>
          </div>
          <div className="events__chips">
            {list
              .sort((a, b) => (a.outreachBy || '').localeCompare(b.outreachBy || ''))
              .map((e) => {
                const s = e.computedTier ? EVENT_TIER_STYLES[e.computedTier] : null
                return (
                  <button
                    className="events__calchip"
                    key={e.id}
                    onClick={() => onOpen(e.id)}
                    style={s ? { borderLeftColor: s.color } : undefined}
                    title={`${e.name}${e.startDate ? ` · runs ${formatDate(e.startDate)}` : ''}`}
                  >
                    <span className="events__caldate">
                      {e.outreachBy ? e.outreachBy.slice(8, 10) : '··'}
                    </span>
                    <span className="events__calname">{e.name}</span>
                    {e.computedTier && (
                      <span className="events__caltier" style={{ color: s!.color }}>
                        {e.computedTier}
                      </span>
                    )}
                  </button>
                )
              })}
          </div>
        </div>
      ))}
    </div>
  )
}

function formatMonth(ym: string): string {
  const [y, m] = ym.split('-')
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

/** Person × event, with a clash warning when two confirmed dates overlap. */
function WhoGoesWhere({ rows, people }: { rows: ScoredEvent[]; people: Person[] }) {
  const byPerson = useMemo(() => {
    const m = new Map<string, { person: Person | null; items: { e: ScoredEvent; a: EventAssignment }[] }>()
    for (const e of rows) {
      for (const a of e.assignments) {
        const cur = m.get(a.personId) ?? {
          person: people.find((p) => p.id === a.personId) ?? null,
          items: [],
        }
        cur.items.push({ e, a })
        m.set(a.personId, cur)
      }
    }
    return [...m.values()]
  }, [rows, people])

  if (byPerson.length === 0) {
    return (
      <div className="empty-note events__empty">
        Nobody is assigned yet. Import the ambassadors into <code>skidaCrmPeople</code> and assign
        them to events — until then every coverage score is computed against an empty roster.
      </div>
    )
  }

  return (
    <div className="events__who">
      {byPerson.map(({ person, items }) => {
        const sorted = [...items].sort((x, y) => (x.e.startDate || '').localeCompare(y.e.startDate || ''))
        return (
          <div className="events__person" key={person?.id ?? items[0].a.personId}>
            <div className="events__personhead">
              <strong>{person?.name || items[0].a.personName}</strong>
              {person?.homeBase.label && (
                <span className="muted"> · {person.homeBase.label}</span>
              )}
            </div>
            {sorted.map(({ e, a }, i) => {
              const prev = sorted[i - 1]
              const clash =
                prev &&
                a.status === 'confirmed' &&
                prev.a.status === 'confirmed' &&
                e.startDate &&
                prev.e.startDate &&
                prev.e.startDate <= (e.endDate || e.startDate) &&
                e.startDate <= (prev.e.endDate || prev.e.startDate)
              return (
                <div className="events__assign" key={a.id}>
                  <span className={`pill events__status events__status--${a.status}`}>{a.status}</span>
                  <span>{e.name}</span>
                  <span className="muted">
                    {e.startDate ? formatDate(e.startDate) : 'no date'}
                  </span>
                  {clash && <span className="events__clash">clashes with {prev.e.name}</span>}
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

function EventDrawer({
  e,
  people,
  onClose,
  onEdit,
}: {
  e: ScoredEvent
  people: Person[]
  onClose: () => void
  /** Opens the event's own details for correction — AVV-2026-001. */
  onEdit: () => void
}) {
  const [saving, setSaving] = useState(false)
  const [notes, setNotes] = useState(e.notes)
  const [addPerson, setAddPerson] = useState('')
  // Notes are the one free-text field, so it is edited locally and saved on
  // blur rather than on every keystroke — a write per character would fight the
  // live subscription that feeds this drawer.
  useEffect(() => setNotes(e.notes), [e.id, e.notes])

  const set = async (patch: Parameters<typeof updateEvent>[1]) => {
    setSaving(true)
    try {
      await updateEvent(e.id, patch)
    } finally {
      setSaving(false)
    }
  }

  const assigned = new Set(e.assignments.map((a) => a.personId))
  const available = people
    .filter((p) => p.active && !assigned.has(p.id))
    .sort((a, b) => a.name.localeCompare(b.name))
  /** The spec makes this a hard gate, so the UI states it rather than hides it. */
  const needsCode = e.stage === 'planned' && !e.trackingCode

  return (
    <div className="events__drawer">
      <button className="btn btn--ghost events__close" onClick={onClose}>
        Close
      </button>
      <h3 className="events__drawertitle">{e.name}</h3>
      <div className="events__meta">
        {e.id} · {e.category} · {e.country}
        {e.region ? ` · ${e.region}` : ''}
        {e.venue ? ` · ${e.venue}` : ''}
        {/* Beside the facts it changes, rather than in the header: everything
            else in this drawer saves as you touch it, so an Edit button at the
            top would suggest the whole drawer was read-only until pressed. */}
        <button className="events__editbtn" onClick={onEdit}>
          Edit details
        </button>
      </div>

      {e.skipped && <div className="events__banner">Not scored — {e.skipped}</div>}
      {needsCode && (
        <div className="events__banner events__banner--warn">
          Live without a tracking code. Without one there is no way to tell afterwards whether it
          worked.
        </div>
      )}

      <div className="events__facts">
        <Fact label="Runs">
          {e.startDate ? `${formatDate(e.startDate)}${e.endDate ? ` – ${formatDate(e.endDate)}` : ''}` : '—'}
          {e.dateConfidence && <span className="muted"> · {e.dateConfidence}</span>}
        </Fact>
        <Fact label="Outreach by">{e.outreachBy ? formatDate(e.outreachBy) : '—'}</Fact>
        <Fact label="Audience">{e.audience || '—'}</Fact>
        <Fact label="Cost tier">{e.costTier || '—'}</Fact>
        <Fact label="Score">
          {e.computedScore === null ? '—' : `${e.computedScore} (${e.computedTier})`}
          {e.computedPerKEur !== null && (
            <span className="muted"> · {e.computedPerKEur} per €1k</span>
          )}
        </Fact>
        <Fact label="Coverage">
          {e.computedCoverage === null ? '—' : e.computedCoverage}
          <span className="muted"> · computed, not editable</span>
        </Fact>
        {e.contactName && <Fact label="Contact">{e.contactName}</Fact>}
        {e.contactEmail && (
          <Fact label="Email">
            <a href={`mailto:${e.contactEmail}`}>{e.contactEmail}</a>
          </Fact>
        )}
        {e.website && (
          <Fact label="Site">
            <a href={e.website} target="_blank" rel="noreferrer">
              {e.website.replace(/^https?:\/\//, '')}
            </a>
          </Fact>
        )}
        <Fact label="Tracking code">{e.trackingCode || <em>none set</em>}</Fact>
      </div>

      <label className="events__field">
        <span>Stage</span>
        <select
          className="input"
          value={e.stage}
          disabled={saving}
          onChange={(ev) => void set({ stage: ev.target.value as EventStage })}
        >
          {EVENT_STAGES.map((st) => (
            <option key={st.key} value={st.key}>
              {st.label}
            </option>
          ))}
        </select>
        <span className="events__hint">
          {EVENT_STAGES.find((st) => st.key === e.stage)?.hint}
        </span>
      </label>

      <div className="events__row2">
        <label className="events__field">
          <span>Owner</span>
          <select
            className="input"
            value={e.ownerId}
            disabled={saving}
            onChange={(ev) => {
              const p = people.find((x) => x.id === ev.target.value)
              void set({ ownerId: ev.target.value, ownerName: p?.name ?? '' })
            }}
          >
            <option value="">nobody</option>
            {people
              .filter((p) => p.active)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>

        <label className="events__field">
          <span>Outreach by</span>
          <input
            className="input"
            type="date"
            value={e.outreachBy}
            disabled={saving}
            onChange={(ev) => void set({ outreachBy: ev.target.value })}
          />
        </label>
      </div>

      <div className="events__row2">
        <label className="events__field">
          <span>Starts</span>
          <input
            className="input"
            type="date"
            value={e.startDate}
            disabled={saving}
            onChange={(ev) => void set({ startDate: ev.target.value })}
          />
        </label>
        <label className="events__field">
          <span>Ends</span>
          <input
            className="input"
            type="date"
            value={e.endDate}
            disabled={saving}
            onChange={(ev) => void set({ endDate: ev.target.value })}
          />
        </label>
      </div>

      <label className="events__field">
        <span>
          Instagram <span className="muted">— the event's own handle, without the @</span>
        </span>
        <input
          className="input"
          value={e.instagram}
          disabled={saving}
          placeholder="ausbackcountryfestival"
          onChange={(ev) => void set({ instagram: ev.target.value.trim().replace(/^@/, '') })}
        />
      </label>

      <label className="events__field">
        <span>
          Tracking code{' '}
          <span className="muted">— required before Planned, or nothing can be measured</span>
        </span>
        <input
          className="input"
          value={e.trackingCode}
          disabled={saving}
          placeholder="BACKFEST20"
          onChange={(ev) => void set({ trackingCode: ev.target.value.toUpperCase() })}
        />
      </label>

      {/* What it cost and who turned up. Kept together and next to the
          tracking code, because all three answer the same question after the
          fact: was this worth doing again. */}
      <div className="events__money">
        <NumField
          label="Budget (planned, kr)"
          value={e.budgetNok}
          disabled={saving}
          onChange={(v) => void set({ budgetNok: v })}
        />
        <NumField
          label="Actual spend (kr)"
          value={e.actualSpendNok}
          disabled={saving}
          onChange={(v) => void set({ actualSpendNok: v })}
        />
        <NumField
          label="Registered"
          value={e.registered}
          disabled={saving}
          onChange={(v) => void set({ registered: v })}
        />
        <NumField
          label="Attendees"
          value={e.attendees}
          disabled={saving}
          onChange={(v) => void set({ attendees: v })}
        />
      </div>

      {/* Cost per head is the number the four fields exist to produce, so it is
          shown rather than left to be worked out. Registered is used only when
          nobody counted who actually came. */}
      {e.actualSpendNok !== null && (e.attendees ?? e.registered) ? (
        <p className="events__perhead">
          {Math.round(e.actualSpendNok / (e.attendees ?? e.registered ?? 1)).toLocaleString('nb-NO')}{' '}
          kr per {e.attendees !== null ? 'attendee' : 'registration'}
          {e.budgetNok !== null &&
            ` · ${e.actualSpendNok <= e.budgetNok ? 'within' : 'over'} budget`}
        </p>
      ) : null}

      {/* Sits with the tracking code because both answer the same question:
          what marketing this event actually produced. Renders nothing when no
          post links here. */}
      <LinkedPosts eventId={e.id} />

      <div className="events__block">
        <div className="card-label">Who is going</div>
        {e.assignments.length === 0 && (
          <p className="muted events__none">
            Nobody yet. Coverage is scored against whoever is assigned, so this is what moves
            the number.
          </p>
        )}
        {e.assignments.map((a) => (
          <div className="events__assignrow" key={a.id}>
            <span className="events__assignname">{a.personName}</span>
            <select
              className="input events__mini"
              value={a.role}
              onChange={(ev) =>
                void upsertAssignment({
                  ...a,
                  role: ev.target.value as EventAssignment['role'],
                })
              }
            >
              <option value="ambassador">ambassador</option>
              <option value="staff">staff</option>
              <option value="partner">partner</option>
            </select>
            <select
              className="input events__mini"
              value={a.status}
              onChange={(ev) =>
                void upsertAssignment({
                  ...a,
                  status: ev.target.value as EventAssignment['status'],
                })
              }
            >
              <option value="proposed">proposed</option>
              <option value="confirmed">confirmed</option>
              <option value="declined">declined</option>
              <option value="attended">attended</option>
            </select>
            <input
              className="input events__mini"
              type="number"
              placeholder="€ cost"
              value={a.costEur ?? ''}
              onChange={(ev) =>
                void upsertAssignment({
                  ...a,
                  costEur: ev.target.value ? Number(ev.target.value) : null,
                })
              }
            />
            <button
              className="btn btn--ghost events__mini"
              onClick={() => void removeAssignment(e.id, a.personId)}
              title="Remove from this event"
            >
              ×
            </button>
          </div>
        ))}

        <div className="events__addrow">
          <select
            className="input"
            value={addPerson}
            onChange={(ev) => setAddPerson(ev.target.value)}
          >
            <option value="">add somebody…</option>
            {available.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.countries.length ? ` — ${p.countries[0]}` : ''}
                {p.role === 'pilot' ? ' (pilot)' : ''}
              </option>
            ))}
          </select>
          <button
            className="btn"
            disabled={!addPerson}
            onClick={() => {
              const p = people.find((x) => x.id === addPerson)
              if (!p) return
              void upsertAssignment({
                eventId: e.id,
                personId: p.id,
                personName: p.name,
                role: p.role === 'pilot' ? 'staff' : 'ambassador',
                status: 'proposed',
                travelFrom: p.homeBase.label,
                costEur: null,
                tasks: [],
                debrief: '',
                leadsCollected: null,
              })
              setAddPerson('')
            }}
          >
            Assign
          </button>
        </div>
      </div>

      <label className="events__field">
        <span>Notes</span>
        <textarea
          className="input events__notes"
          rows={5}
          value={notes}
          onChange={(ev) => setNotes(ev.target.value)}
          onBlur={() => notes !== e.notes && void set({ notes })}
          placeholder="What we know, what was agreed, why the reach number is what it is."
        />
      </label>

      <label className="events__field">
        <span>
          Reach (1–5) <span className="muted">— set by hand, reasoning in the notes</span>
        </span>
        <select
          className="input"
          value={e.reach ?? ''}
          disabled={saving}
          onChange={(ev) =>
            void set({ reach: ev.target.value ? Number(ev.target.value) : null })
          }
        >
          <option value="">not set</option>
          {[1, 2, 3, 4, 5].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      </label>

      {e.nextAction && (
        <div className="events__block">
          <div className="card-label">Next action</div>
          <p>{e.nextAction}</p>
        </div>
      )}
      {e.sponsorshipModel && (
        <div className="events__block">
          <div className="card-label">Sponsorship model</div>
          <p>{e.sponsorshipModel}</p>
        </div>
      )}
      {/* Decision 64. The columns that came off the table are provenance, not
          planning, and they now have a home together rather than being spread
          through the record or lost. Working stage is here for the same reason:
          it is where the harvest put the row, which is a different question
          from the status the table shows. */}
      <div className="events__block">
        <div className="card-label">How it entered the book</div>
        <dl className="events__provenance">
          <div>
            <dt>Source</dt>
            <dd className={e.sourceVerified ? undefined : 'muted'}>
              {e.sourceVerified || 'none recorded'}
            </dd>
          </div>
          <div>
            <dt>Working stage</dt>
            <dd className={e.importStage ? undefined : 'muted'}>
              {e.importStage || 'not set by the import'}
            </dd>
          </div>
          <div>
            <dt>Date confidence</dt>
            <dd className={e.dateConfidence ? undefined : 'muted'}>
              {e.dateConfidence || 'not stated'}
            </dd>
          </div>
          <div>
            <dt>Region</dt>
            <dd className={e.region ? undefined : 'muted'}>{e.region || 'not stated'}</dd>
          </div>
          <div>
            <dt>Cost tier</dt>
            <dd className={e.costTier ? undefined : 'muted'}>{e.costTier || 'not stated'}</dd>
          </div>
          <div>
            <dt>Outreach by</dt>
            <dd className={e.outreachBy ? undefined : 'muted'}>
              {e.outreachBy ? formatDate(e.outreachBy) : 'no deadline'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="events__fact">
      <span className="events__factlabel">{label}</span>
      <span>{children}</span>
    </div>
  )
}

/**
 * A number that may legitimately be unset.
 *
 * An empty input means "not recorded" and writes null, not zero — a zero budget
 * scores as free, and free is a claim nobody made.
 */
function NumField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string
  value: number | null
  disabled: boolean
  onChange: (v: number | null) => void
}) {
  return (
    <label className="events__field">
      <span>{label}</span>
      <input
        className="input"
        type="number"
        min={0}
        value={value ?? ''}
        disabled={disabled}
        placeholder="not recorded"
        onChange={(ev) => onChange(ev.target.value === '' ? null : Number(ev.target.value))}
      />
    </label>
  )
}
